// src/ui/steps/step3-festival-dienstverband.jsx, Festival loondienst-flow stap 3
// Periodiek (0–15) bepaalt bruto fulltime maandsalaris binnen de gekozen schaal.
// Dienstverband = lijst van blokken (aantal maanden × deeltijd-percentage). Bruto/maand
// per blok = bruto_fulltime × pct/100. Gemiddeld bruto over alle maanden = gewogen.
import React from "react";
import {
  FW, FwButton, FwInput, FwSelect,
  StepHeader, NiveauBadge, AsideCard,
} from "../primitives";
import { calc } from "../../lib/calc";
import { config } from "../../lib/config";

const nieuwBlok = (maanden = 12, pct = 100) => ({
  id: window.crypto?.randomUUID?.() || `blok-${Date.now()}-${Math.random()}`,
  maanden, pct,
});

export const StepFestivalDienstverband = ({ state, setState, next, back }) => {
  const huidigeRol = state.rollen?.[0];

  const beschikbarePeriodieken = React.useMemo(() => {
    if (!huidigeRol) return [];
    return (config.tarieven_festival_loondienst || [])
      .filter(t => t.niveau === huidigeRol.niveau)
      .map(t => Number(t.periodiek))
      .sort((a, b) => a - b);
  }, [huidigeRol?.niveau]);

  const [periodiek, setPeriodiek] = React.useState(
    huidigeRol?.periodiek != null ? huidigeRol.periodiek : (beschikbarePeriodieken[0] ?? 0)
  );

  const [blokken, setBlokken] = React.useState(() => {
    const bestaand = huidigeRol?.dienstverband;
    if (Array.isArray(bestaand) && bestaand.length) {
      return bestaand.map(b => ({ ...b, id: b.id || nieuwBlok().id }));
    }
    return [nieuwBlok(12, 100)];
  });

  React.useEffect(() => {
    if (!huidigeRol) back();
  }, []);

  React.useEffect(() => {
    setState(s => ({
      ...s,
      rollen: (s.rollen || []).map((r, i) => i === 0 ? {
        ...r,
        periodiek: Number(periodiek),
        dienstverband: blokken.map(b => ({
          id: b.id,
          maanden: Math.max(0, Number(b.maanden) || 0),
          pct: Math.max(0, Math.min(100, Number(b.pct) || 0)),
        })),
      } : r),
    }));
  }, [periodiek, blokken]);

  if (!huidigeRol) return null;

  const tariefRij = calc.tariefFestivalLoondienst(huidigeRol.niveau, periodiek);
  const brutoFulltime = Number(tariefRij?.bruto_fulltime) || 0;

  const updateBlok = (id, patch) => {
    setBlokken(bs => bs.map(b => b.id === id ? { ...b, ...patch } : b));
  };
  const verwijderBlok = (id) => {
    setBlokken(bs => bs.filter(b => b.id !== id));
  };
  const voegToe = () => {
    setBlokken(bs => [...bs, nieuwBlok(0, 100)]);
  };

  const totaalMaanden = blokken.reduce((s, b) => s + (Math.max(0, Number(b.maanden)) || 0), 0);
  const totaalBruto = blokken.reduce((s, b) => {
    const m = Math.max(0, Number(b.maanden)) || 0;
    const p = Math.max(0, Math.min(100, Number(b.pct))) || 0;
    return s + (brutoFulltime * (p / 100) * m);
  }, 0);
  const gemBruto = totaalMaanden > 0 ? totaalBruto / totaalMaanden : 0;

  // brutoFulltime > 0 is al gegarandeerd door de schaal-keuze in stap 3.1;
  // hier alleen op gebruikersinvoer (totaalMaanden) checken, conform de
  // werkdagen-/timesheet-validatie in de andere flows.
  const valid = totaalMaanden > 0;

  return (
    <div>
      <StepHeader
        tag="Filmfestival"
        vorm={state.vorm}
        title="Hoe ziet jouw dienstverband eruit?"
        sub="De salarisschalen voor festivalmedewerkers en de daarvan afgeleide zzp-starttarieven zijn gebaseerd op de Cao Toneel & Dans. Je bruto maandsalaris bij een fulltime dienstverband hangt af van je salarisschaal en je periodiek. Je periodiek is het aantal ervaringsjaren binnen die functie en schaal. Geef ook aan hoe je contract per periode is opgebouwd. Splits je dienstverband op in blokken met een duur en een percentage. Bijvoorbeeld: 4 maanden – 40% dienstverband en 4 maanden – 80% dienstverband."
        onBack={back}
      />

      <div className="role-summary" style={{
        display: "flex", gap: 14, alignItems: "center", flexWrap: "wrap",
        padding: "14px 18px", marginBottom: 20,
        background: FW.cream, border: `1.5px solid ${FW.gray100}`, borderRadius: 14,
      }}>
        <div style={{ fontSize: 14, color: FW.gray500, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5 }}>Je rol</div>
        <div style={{ fontSize: 16, fontWeight: 700, color: FW.navy }}>{huidigeRol.rol}</div>
        <div style={{ fontSize: 14, color: FW.navy2 }}>· {huidigeRol.dep}</div>
        <NiveauBadge niveau={huidigeRol.niveau} />
        <div className="role-summary-spacer" style={{ marginLeft: "auto", fontSize: 14, color: FW.navy2 }}>
          Bruto fulltime: <b>{calc.fmt(brutoFulltime)}</b><span style={{ color: FW.gray500 }}> /maand</span>
        </div>
      </div>

      <div className="row-2col" style={{ display: "grid", gridTemplateColumns: "1.3fr 1fr", gap: 24, alignItems: "start" }}>
        <div style={{ display: "grid", gap: 20 }}>
          <div style={{
            padding: 20, background: FW.white,
            border: `1.5px solid ${FW.gray100}`, borderRadius: 18,
          }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: FW.navy, marginBottom: 8 }}>Periodiek</div>
            <div style={{ fontSize: 13, color: FW.gray500, marginBottom: 10 }}>
              Aantal jaren ervaring binnen schaal {huidigeRol.niveau}. Begin je net? Kies 0. Elk jaar in dezelfde functie en schaal stijg je één periodiek op.
            </div>
            <FwSelect
              value={periodiek}
              onChange={(v) => setPeriodiek(Number(v))}
              ariaLabel="Periodiek"
              options={beschikbarePeriodieken.map(p => {
                const t = calc.tariefFestivalLoondienst(huidigeRol.niveau, p);
                return {
                  value: p,
                  label: `Periodiek ${p}: bruto fulltime ${calc.fmt(Number(t?.bruto_fulltime) || 0)}`,
                };
              })}
            />
            <div style={{ fontSize: 12, color: FW.gray500, marginTop: 8 }}>
              * Bruto salaris op basis van 38 uur.
            </div>
          </div>

          <div style={{
            padding: 20, background: FW.white,
            border: `1.5px solid ${FW.gray100}`, borderRadius: 18,
          }}>
            <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", marginBottom: 4 }}>
              <div style={{ fontSize: 18, fontWeight: 700, color: FW.navy }}>Dienstverband-blokken</div>
              <div style={{ fontSize: 14, color: FW.navy2 }}>
                Totaal: <b style={{ color: FW.navy }}>{totaalMaanden} mnd</b>
              </div>
            </div>
            <div style={{ fontSize: 13, color: FW.gray500, marginBottom: 14 }}>
              Voeg een blok toe voor elke aaneengesloten periode met hetzelfde percentage.
            </div>

            <div style={{ display: "grid", gap: 10 }}>
              {blokken.map((b, i) => {
                const m = Math.max(0, Number(b.maanden)) || 0;
                const p = Math.max(0, Math.min(100, Number(b.pct))) || 0;
                const brutoMaand = brutoFulltime * (p / 100);
                return (
                  <div key={b.id} style={{
                    display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap",
                    padding: "10px 12px", background: FW.cream,
                    borderRadius: 12, border: `1px solid ${FW.gray100}`,
                  }}>
                    <span style={{ fontSize: 13, fontWeight: 700, color: FW.gray500, minWidth: 24 }}>#{i + 1}</span>
                    <input
                      type="number" min="0" max="120"
                      value={b.maanden}
                      onChange={e => updateBlok(b.id, { maanden: e.target.value })}
                      style={inputCel(72)}
                    />
                    <span style={{ fontSize: 13, color: FW.gray500 }}>maanden</span>
                    <span style={{ fontSize: 13, color: FW.gray500 }}>·</span>
                    <input
                      type="number" min="0" max="100"
                      value={b.pct}
                      onChange={e => updateBlok(b.id, { pct: e.target.value })}
                      style={inputCel(64)}
                    />
                    <span style={{ fontSize: 13, color: FW.gray500 }}>% van fulltime</span>
                    <span style={{
                      marginLeft: "auto", fontSize: 14, color: FW.navy,
                      fontVariantNumeric: "tabular-nums",
                    }}>
                      = <b>{calc.fmt(brutoMaand)}</b><span style={{ color: FW.gray500 }}> /mnd</span>
                    </span>
                    {blokken.length > 1 && (
                      <button onClick={() => verwijderBlok(b.id)} title="Verwijder blok" style={{
                        background: "transparent", border: 0, cursor: "pointer",
                        color: FW.gray500, fontSize: 18, padding: 4,
                      }}>×</button>
                    )}
                  </div>
                );
              })}
            </div>

            <button onClick={voegToe} style={{
              marginTop: 12, background: "transparent", border: `1.5px dashed ${FW.gray100}`, color: FW.navy,
              borderRadius: 10, padding: "8px 14px", fontSize: 13, fontWeight: 700,
              cursor: "pointer", fontFamily: "var(--font-sans)",
            }}>
              + Voeg dienstverband-blok toe
            </button>
          </div>
        </div>

        <AsideCard>
          <div style={{ fontSize: 14, fontWeight: 700, color: FW.navy, marginBottom: 10, display: "inline-flex", alignItems: "center", gap: 6 }}>
            <i className="ri-calculator-line" /> Lopend totaal
          </div>
          <div style={{ display: "grid", gap: 6, fontSize: 14, color: FW.navy2 }}>
            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <span>Periodiek</span>
              <span style={{ fontWeight: 700, color: FW.navy }}>{periodiek}</span>
            </div>
            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <span>Bruto fulltime</span>
              <span style={{ color: FW.navy }}>{calc.fmt(brutoFulltime)}</span>
            </div>
            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <span>Periode</span>
              <span style={{ fontWeight: 700, color: FW.navy }}>{totaalMaanden} mnd</span>
            </div>
          </div>
          <div style={{ height: 1, background: FW.gray100, margin: "12px 0" }} />
          <div style={{
            padding: "14px 16px", background: FW.navy, borderRadius: 14,
            display: "grid", gap: 4,
          }}>
            <span style={{ fontSize: 13, color: FW.yellow, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5 }}>
              Gemiddeld bruto / maand
            </span>
            <span style={{ fontSize: 22, fontWeight: 700, color: "#fff", fontVariantNumeric: "tabular-nums" }}>
              {calc.fmtInt(gemBruto)}
            </span>
            <span style={{ fontSize: 12, color: "rgba(255,255,255,.65)" }}>
              Totaal periode: <b style={{ color: "#fff" }}>{calc.fmtInt(totaalBruto)}</b>
            </span>
          </div>
          <div style={{ fontSize: 12, color: FW.gray500, marginTop: 8, lineHeight: 1.4 }}>
            Gewogen op aantal maanden. Secundaire voorwaarden (vakantiegeld, pensioenbijdrage, vertrouwenspersoon) komen in stap 5 als onderhandelpunten.
          </div>
        </AsideCard>
      </div>

      <div className="step-nav" style={{ marginTop: 32, display: "flex", justifyContent: "space-between" }}>
        <FwButton variant="ghost" arrow={false} onClick={back}>← Terug</FwButton>
        <FwButton variant="primary" onClick={next} disabled={!valid}>
          Bekijk je salarisindicatie
        </FwButton>
      </div>
    </div>
  );
};

const inputCel = (w) => ({
  width: w, border: `1.5px solid ${FW.gray100}`, borderRadius: 8,
  padding: "6px 8px", fontSize: 14, fontWeight: 700, color: FW.navy,
  fontFamily: "var(--font-sans)", textAlign: "center", outline: "none", background: FW.white,
});
